import React, { memo, useCallback, useMemo, useEffect, Fragment, useState } from 'react';
import { css } from '@emotion/react';
import { tap } from 'rxjs/operators';
import { ModalHeader, ModalContent, ModalFooter, ModalAction, ModalDrawer } from '@core/components/Modal';
import { useModalInstance } from '@core/components/ModalStack';
import { useFormConfig, formGroup, formControl, useFormState, Validators, FormControl, FormDataType, formArray, FormArray } from '@core/utils/form';
import { useBloc } from '@core/utils/bloc';
import { FormViewModalBloc } from './FormViewModalBloc';
import { FormValueHistoryResponse } from '@business/entities/form';
import { t } from 'ttag';
import { FCFormDataType, FCType } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/types';
import { R } from '@core/utils/r';
import {
  FCTextFormData,
  FCDateFormData,
  FCParagraphFormData,
  FCDropdownFormData,
  FCUploadFormData,
  FCPhoneFormData,
  FCCheckboxFormData,
  FCRadioFormData,
  FCDescriptionFormData,
  FCTitleFormData,
  FCSignatureFormData,
  FCSignaturePreview,
} from '@modules/settings/SettingsPage/Components/FormsTab/FormFields';
import {
  FormInput,
  FormDateInput,
  FormSelect,
  FormCheckboxGroup,
  FormCheckboxGruped,
  FormText,
  FormRadioMaterial,
  FormContainer,
  FormInputPhone,
} from '@core/components/form';
import { FormHTML } from '@core/components/form/FormHTML';
import { FormUpload } from '@core/components/form/FormUpload';
import { CommonService, HTMLService } from '@business/services';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { Label, Media, CardTitle, Badge, Table, UncontrolledTooltip, Card, CardBody } from 'reactstrap';
import { FileFormResponse, FormShareResponse } from '@business/entities/file';
import { Formatter } from '@core/utils/formatter';
import { WithTooltip, HTMLErrorBoundary } from '@modules/common';
import { openFormShareModal } from '../FormSharesModal';
import { UploadResponse, TenantIdentity } from '@business/entities/common';
import { ProcessStep } from './ProcessStep';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useDidMount } from '@core/utils/hooks/react';
import { TextBlock } from '@modules/common/TextBlock';
import { toMoment } from '@core/utils/time';
import { DATE_FORMAT } from '@business/constants/time';
import { LibraryService } from '@business/services/library_service';
import { LibraryItemStatus, LibraryItemType } from '@business/entities/library';
import { SRLWrapper } from 'simple-react-lightbox';
import { AnimatedItems } from '@modules/public/PresentationPage/Components/AnimatedItems';
import { Button } from '@core/components/Button';
import { CustomTabFilesResponse } from '@business/entities/custom-tab';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UploadAPI } from '@business/api/upload';
import { createLocalDraftStore, useDraftAutosave } from '@modules/common';
import { InfoBanner } from '@modules/common/InfoBanner';
import { LocalDraft } from '@business/entities/common';
import { Session } from '@modules/auth/session';
import { useCompany } from '@core/utils/hooks/misc';

export function defineCustomForm(fields: FCFormDataType[]) {
  return formGroup({
    ...R.fromPairs(fields.map((field, index) => [`${index}`, getFieldControl(field)])),
  });
}

export type CustomFormData = FormDataType<typeof defineCustomForm>;

// Helper function to upload base64 signature and return Observable
function uploadSignatureIfNeeded(value: string): Observable<string> {
  if (value && value.startsWith('data:image')) {
    // It's base64, need to upload
    return new Observable<string>(observer => {
      fetch(value)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
          UploadAPI.upload('forms', file, false).subscribe({
            next: response => {
              observer.next(response.fileUrl);
              observer.complete();
            },
            error: err => observer.error(err),
          });
        })
        .catch(err => observer.error(err));
    });
  }
  // Already a URL or empty
  return of(value);
}

// Process all signature fields in form data
function processSignatures(data: CustomFormData, fields: FCFormDataType[], form?: any): Observable<CustomFormData> {
  const processedData = { ...data };
  const uploads: Observable<any>[] = [];

  // First, validate and save any unsaved signatures from canvas to control
  const signatureErrors: string[] = [];

  fields.forEach((field, i) => {
    if (field.type === FCType.signature && form && form.controls[`${i}`]) {
      const control = form.controls[`${i}`];
      const fieldConfig = field as FCSignatureFormData;

      // Save signature from canvas first
      if ((control as any)._saveSignature) {
        (control as any)._saveSignature();
        // Update processedData with the newly saved value
        processedData[i] = control.value;
      }

      // Validate if required
      if (fieldConfig.isRequired && !control.value) {
        const isEmpty = (control as any)._signatureValidator ? (control as any)._signatureValidator() !== null : true;
        if (isEmpty) {
          signatureErrors.push(fieldConfig.title || `Signature field ${i + 1}`);
        }
      }
    }
  });

  // If there are signature validation errors, return error observable
  if (signatureErrors.length > 0) {
    console.error('Signature validation failed:', signatureErrors.join(', ') + ' is required');
    return new Observable(observer => {
      observer.error(new Error(`Please sign: ${signatureErrors.join(', ')}`));
    });
  }

  // Then upload signatures that need uploading
  fields.forEach((field, i) => {
    if (field.type === FCType.signature && processedData[i]) {
      const upload$ = uploadSignatureIfNeeded(processedData[i]).pipe(
        tap(url => {
          processedData[i] = url;
        })
      );
      uploads.push(upload$);
    }
  });

  // If no uploads needed, return immediately
  if (uploads.length === 0) {
    return of(processedData);
  }

  // Wait for all uploads to complete
  return forkJoin(uploads).pipe(map(() => processedData));
}

function defineProcessForm() {
  return formGroup({
    content: formControl<string>({ initialValue: 'Your content here' }),
    type: formControl<string>({ initialValue: 'image' }),
    image: formControl<string>({}),
    videoUrl: formControl<string>({ label: 'Video Url' }),
    embedUrl: formControl<string>({ label: 'Video Url' }),
  });
}

export type ProcessFormType = ReturnType<typeof defineProcessForm>;
export type ProcessFormData = FormDataType<typeof defineProcessForm>;

export function getFieldControl(field: FCFormDataType) {
  if (field.type === FCType.text) {
    const f = field as FCTextFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      validators: [Validators.min(f.minValueLength)],
    });
  } else if (field.type === FCType.date) {
    const f = field as FCDateFormData;
    return formControl<number>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.email) {
    const f = field as FCTextFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      validators: [Validators.email],
    });
  } else if (field.type === FCType.paragraph) {
    const f = field as FCParagraphFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      validators: [Validators.min(f.minValueLength)],
    });
  } else if (field.type === FCType.dropdown) {
    const f = field as FCDropdownFormData;
    if (f.addMultiple) {
      return formControl<string[]>({
        required: f.isRequired,
        label: f.title,
        placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      });
    }
    return formControl<string>({
      
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.upload) {
    const f = field as FCUploadFormData;
    return formControl<UploadResponse>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.phone) {
    const f = field as FCPhoneFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.checkbox) {
    const f = field as FCCheckboxFormData;
    return formControl<string[]>({
      required: f.isRequired,
      label: f.title,
    });
  } else if (field.type === FCType.radio) {
    const f = field as FCRadioFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
    });
  } else if (field.type === FCType.desciption) {
    return formControl<string>({});
  } else if (field.type === FCType.title) {
    return formControl<string>({});
  } else if (field.type === FCType.process) {
    return formArray(() => defineProcessForm());
  } else if (field.type === FCType.signature) {
    const f = field as FCSignatureFormData;
    return formControl<string>({
      required: false, // Don't validate here, validate manually before submit
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  }

  return formControl<string>({});
}

export function getFieldJSX(
  field: FCFormDataType,
  index: number,
  inControl: FormControl<any> | FormArray<any>,
  options?: { disabled?: boolean; material?: boolean; isApplicationCompleted?: boolean },
  isPrivate: boolean = false,
) {
  const { disabled = false, isApplicationCompleted = false } = options || {};
  const control = inControl as FormControl<any>;
  if (field.type === FCType.text || field.type === FCType.email) {
    return <FormInput key={index} control={control} disabled={disabled} description={R.get(field, 'description')} />;
  } else if (field.type === FCType.date) {
    return <FormDateInput key={index} control={control} disabled={disabled} description={R.get(field, 'description')} />;
  } else if (field.type === FCType.paragraph) {
    const f = field as FCParagraphFormData;
    if (f.showTextEditor) {
      return <FormHTML control={control} disabled={disabled} description={R.get(field, 'description')} />;
    }
    return <FormInput type="textarea" key={index} control={control} disabled={disabled} description={R.get(field, 'description')} />;
  } else if (field.type === FCType.dropdown) {
    const f = field as FCDropdownFormData;
    const options = f.options.split('\n');
    const optionItems = CommonService.selectItems(
      options,
      o => o,
      o => o,
      false,
    );
    return (
      <FormSelect
        type={f.addMultiple ? 'multiple' : 'single'}
        control={control}
        items={optionItems}
        key={index}
        disabled={disabled}
        description={R.get(field, 'description')}
      />
    );
  } else if (field.type === FCType.upload) {
    return (
      <FormUpload
        key={index}
        control={control}
        type="single"
        autoUpload
        autoUploadKey={`forms`}
        disabled={disabled}
        description={R.get(field, 'description')}
        isPrivate={isPrivate}
      />
    );
  } else if (field.type === FCType.phone) {
    // Proper phone input defaulting to United States (+1); the flag dropdown lets the user
    // pick another country. Was a plain text input before, so numbers landed without any code.
    const phoneDescription = R.get(field, 'description');
    return (
      <Fragment key={index}>
        <FormInputPhone control={control} disabled={disabled} />
        {!!phoneDescription && <small className="text-muted d-block mb-2">{phoneDescription}</small>}
      </Fragment>
    );
  } else if (field.type === FCType.checkbox) {
    const f = field as FCCheckboxFormData;
    const items = f.options.split('\n');

    const description = R.get(field, 'description');
    const uid = inControl.uid.replace('form:', '');
    const toolip = (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip placement="top" target={`control-description-${uid}`}>
          {description}
        </UncontrolledTooltip>
      </Fragment>
    );

    return (
      <Fragment key={index}>
        <Label>{f.title}</Label>
        {!!description && toolip}
        <FormCheckboxGroup control={control} disabled={disabled}>
          {items.map((option, order) => {
            return <FormCheckboxGruped label={option} value={option} key={order} disabled={disabled} />;
          })}
        </FormCheckboxGroup>
      </Fragment>
    );
  } else if (field.type === FCType.radio) {
    const f = field as FCRadioFormData;
    const items = f.options.split('\n');

    const description = R.get(field, 'description');
    const uid = inControl.uid.replace('form:', '');

    const toolip = (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip placement="top" target={`control-description-${uid}`}>
          {description}
        </UncontrolledTooltip>
      </Fragment>
    );

    if (options && options.material) {
      return (
        <Fragment key={index}>
          <Label>{f.title}</Label>
          {!!description && toolip}
          <FormRadioGroup control={control}>
            <AnimatedItems direction="up">
              {items.map((option, index) => (
                <FormRadioMaterial label={option} value={option} key={index} disabled={disabled} />
              ))}
            </AnimatedItems>
          </FormRadioGroup>
        </Fragment>
      );
    }

    return (
      <Fragment key={index}>
        <Label>{f.title}</Label>
        {!!description && toolip}
        <FormRadioGroup control={control} disabled={disabled}>
          {items.map((option, order) => (
            <FormRadio label={option} value={option} key={order} disabled={disabled} />
          ))}
        </FormRadioGroup>
      </Fragment>
    );
  } else if (field.type === FCType.desciption) {
    const f = field as FCDescriptionFormData;
    return HTMLService.toReact(f.description);
  } else if (field.type === FCType.title) {
    const f = field as FCTitleFormData;
    return (
      <div className="mt-4">
        <CardTitle>{f.title}</CardTitle>
        <hr />
      </div>
    );
  } else if (field.type == FCType.process) {
    return <ProcessStep form={control as any as FormArray<any>} />;
  } else if (field.type === FCType.signature) {
    const f = field as FCSignatureFormData;
    return <FCSignaturePreview key={index} {...f} control={control} disabled={disabled || isApplicationCompleted} />;
  }

  return <Fragment></Fragment>;
}

const styles = {
  content: css`
    padding: 25px !important;
  `,

  title: css`
    .MuiTypography-root {
      height: 50px;
      align-items: center;
      position: relative;
      padding: 0 10px 0 20px;
    }
  `,
};

interface HistoryItemProps {
  history: FormValueHistoryResponse;
}
const HistoryItem = memo((props: HistoryItemProps) => {
  const { history } = props;
  const description = useMemo(() => {
    const prefix = history.type === 'created' ? 'Created by' : history.type === 'shared' ? 'Shared by' : 'Updated by';
    const description = `${prefix} ${CommonService.getFullName(history.user)}`;
    const email = (R.isArray(history.values) && R.first(history.values)) || '';
    return history.type === 'shared' ? description + ' with ' + email : description;
  }, [history]);

  return (
    <li className="event-list" style={{ paddingBottom: 10 }}>
      <div className="event-timeline-dot">
        <i className="bx bx-right-arrow-circle font-size-18" />
      </div>

      <Media>
        <div className="mr-3">
          <h6 className="font-size-12">
            {Formatter.formatTime(history.created)} <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ml-2" />
            <br />
            <span style={{ fontSize: 12 }} className="text-muted">
              {Formatter.formatTime(history.created, 'hh:mm a')}
            </span>
          </h6>
        </div>
        <Media body>
          <div>{description}</div>
        </Media>
      </Media>
    </li>
  );
});

export interface FormViewModalProps {
  title: string;
  fileForm: FileFormResponse;
  identity?: TenantIdentity;
  onSave?: (values: string) => Observable<any>;
  isPrivate?: boolean;
  isFooterShow?: boolean;
  isFieldDisabled?: boolean;
}

export const FormViewModal = memo((props: FormViewModalProps) => {
  const { fileForm, identity, isPrivate = false, isFooterShow = true, isFieldDisabled = false } = props;
  const bloc = useBloc(FormViewModalBloc, identity);
  const form = useMemo(() => defineCustomForm(fileForm.fields), [fileForm.fields]);
  const modal = useModalInstance();
  const [sharedItems, setSharedItems] = useState(fileForm.shares || []);
  const company = useCompany();

  // Local draft (localStorage): autosaves what the user fills in so a lost connection,
  // failed save, crash, or accidental close never loses the entered data.
  // One draft per form (deterministic id), stored per company + user.
  const draftStore = useMemo(
    () => createLocalDraftStore(`gc-formfill-drafts-${company.id}-${Session.user?.id ?? 'anon'}`),
    [company.id],
  );
  const draftId = `form-${fileForm.id}`;
  const [draft, setDraft] = useState<LocalDraft | null>(() => (!isFieldDisabled ? draftStore.load().find(d => d.id === draftId) ?? null : null));

  const saveDraftValue = useCallback((value: Record<string, any>) => draftStore.upsert(draftId, value), [draftStore, draftId]);
  const draftAutosave = useDraftAutosave(form, !isFieldDisabled, saveDraftValue);

  const restoreDraft = useCallback(() => {
    if (!draft) return;
    form.patchValue(draft.value as Partial<CustomFormData>);
    form.setTouched(true);
    setDraft(null);
  }, [draft, form]);

  const deleteDraft = useCallback(() => {
    draftStore.remove(draftId);
    setDraft(null);
  }, [draftStore, draftId]);

  const onSubmit = useCallback(
    (data: CustomFormData) => {
      return processSignatures(data, fileForm.fields).pipe(
        switchMap(processedData => {
          if (props && props.onSave) {
            return props.onSave(JSON.stringify(processedData));
          }
          return bloc.saveFormValues(fileForm.id, JSON.stringify(processedData), identity);
        }),
        tap(() => {
          // Saved — stop autosaving and drop the local draft so it can't resurface.
          draftAutosave.stop();
          draftStore.remove(draftId);
        }),
        tap(() => modal.close()),
      );
    },
    [bloc, fileForm.fields, fileForm.id, identity, modal, props, draftAutosave, draftStore, draftId],
  );

  useEffect(() => {
    if (fileForm.values) {
      form.patchValue(JSON.parse(fileForm.values));
    }
  }, [fileForm.values, form]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  const onShareManaged = useCallback(
    (items: FormShareResponse[]) => {
      setSharedItems(items);
      bloc.reloadFile(fileForm.id).subscribe();
    },
    [bloc, fileForm.id],
  );

  return (
    <ModalDrawer maxWidth="lg">
      <ModalHeader
        title={props.title}
        titleStyle={{ flex: 'inherit' }}
        rightContent={
          <div>
            {sharedItems.length > 0 && (
              <div style={{ cursor: 'pointer' }} onClick={() => openFormShareModal({ items: sharedItems, onDone: onShareManaged })}>
                <WithTooltip
                  tooltip={
                    <div>
                      {fileForm.shares.map(i => (
                        <div key={i.id}>{i.email}</div>
                      ))}
                    </div>
                  }
                >
                  <Badge className="ml-2" color="success">
                    Shared Publically
                  </Badge>
                </WithTooltip>
              </div>
            )}
          </div>
        }
      />
      <ModalContent formContainer={{ onSubmit: form.submit }} customCss={styles.content}>
        {draft && (
          <InfoBanner intent="info" icon="fa-history">
            <span style={{ flex: 1 }}>{t`You have unsaved changes for this form from ${new Date(draft.savedAt).toLocaleString()}. Restore them?`}</span>
            <Button type="button" size="small" intent="primary" extraClassNames="ml-2" onClick={restoreDraft}>
              {t`Restore`}
            </Button>
            <Button type="button" size="small" intent="light" extraClassNames="ml-2" onClick={deleteDraft}>
              {t`Discard`}
            </Button>
          </InfoBanner>
        )}
        {fileForm.fields.map((field, index) => getFieldJSX(field, index, form.controls[`${index}`], { disabled: isFieldDisabled }, isPrivate))}
        <hr />
        <ul className="verti-timeline list-unstyled">
          {fileForm.history.map(history => (
            <HistoryItem history={history} key={history.id} />
          ))}
        </ul>
      </ModalContent>
      {isFooterShow && (
        <ModalFooter>
          {!!fileForm.fields.length && <ModalAction intent="primary" text={t`Save`} loading={submitting} autoClose={false} onClick={form.submit} />}
        </ModalFooter>
      )}
    </ModalDrawer>
  );
});

export interface LibraryDocumentModalProps {
  itemId: string;
  onClose?: () => void;
}

export const LibraryDocumentModal = memo((props: LibraryDocumentModalProps) => {
  const { itemId, onClose } = props;
  const bloc = useBloc(FormViewModalBloc);

  const file = useObservable(bloc.file$);
  const titleControl = useMemo(() => formControl<string>({ initialValue: file?.name }), [file]);
  const titleControlValue = useNonNilObservable(titleControl.value$);
  const statusControl = useMemo(() => formControl<LibraryItemStatus>({ initialValue: file ? file.status : LibraryItemStatus.draft }), [file]);
  const statusControlValue = useNonNilObservable(statusControl.value$);

  const form = useMemo(() => defineCustomForm(file?.fields || []), [file]);
  const modal = useModalInstance();
  const company = useCompany();

  // Local draft (localStorage): one draft per library item, same mechanics as FormViewModal.
  const draftStore = useMemo(
    () => createLocalDraftStore(`gc-formfill-drafts-${company.id}-${Session.user?.id ?? 'anon'}`),
    [company.id],
  );
  const draftId = `library-${itemId}`;
  const [draft, setDraft] = useState<LocalDraft | null>(() => draftStore.load().find(d => d.id === draftId) ?? null);

  const saveDraftValue = useCallback((value: Record<string, any>) => draftStore.upsert(draftId, value), [draftStore, draftId]);
  const draftAutosave = useDraftAutosave(form, !!file, saveDraftValue);

  const restoreDraft = useCallback(() => {
    if (!draft) return;
    form.patchValue(draft.value as Partial<CustomFormData>);
    form.setTouched(true);
    setDraft(null);
  }, [draft, form]);

  const deleteDraft = useCallback(() => {
    draftStore.remove(draftId);
    setDraft(null);
  }, [draftStore, draftId]);

  useDidMount(() => {
    bloc
      .fetchFile(itemId)
      .pipe(tap(f => titleControl.setValue(f.name)))
      .subscribe();
  });

  const onSubmit = useCallback(
    (data: CustomFormData) => {
      return bloc
        .updateItem(itemId, {
          type: LibraryItemType.custom,
          name: titleControlValue || file!.name,
          status: statusControlValue || LibraryItemStatus.draft,
          content: data as any,
        })
        .pipe(
          tap(() => {
            draftAutosave.stop();
            draftStore.remove(draftId);
          }),
          tap(() => modal.close()),
          tap(onClose),
        );
    },
    [bloc, file, itemId, modal, onClose, statusControlValue, titleControlValue, draftAutosave, draftStore, draftId],
  );

  useEffect(() => {
    if (file && file.content) {
      form.patchValue(JSON.parse(file.content as any as string));
    }
  }, [file, form]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });
  if (!file) return null;

  return (
    <ModalDrawer maxWidth="xl">
      <ModalHeader customCss={styles.title} titleStyle={{ flex: 1 }}>
        <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <FormText singleLine toolbar={false} control={titleControl} />
          </div>
          <div className="mr-2" style={{ width: 400 }}>
            <FormSelect
              withoutGroup
              control={statusControl}
              items={CommonService.selectItems([LibraryItemStatus.private, LibraryItemStatus.draft, LibraryItemStatus.public], c =>
                LibraryService.getStatusLabel(c),
              )}
            />
          </div>
        </div>
      </ModalHeader>

      <ModalContent formContainer={{ onSubmit: form.submit }} customCss={styles.content}>
        {draft && (
          <InfoBanner intent="info" icon="fa-history">
            <span style={{ flex: 1 }}>{t`You have unsaved changes for this document from ${new Date(draft.savedAt).toLocaleString()}. Restore them?`}</span>
            <Button type="button" size="small" intent="primary" extraClassNames="ml-2" onClick={restoreDraft}>
              {t`Restore`}
            </Button>
            <Button type="button" size="small" intent="light" extraClassNames="ml-2" onClick={deleteDraft}>
              {t`Discard`}
            </Button>
          </InfoBanner>
        )}
        {(file.fields || []).map((field, index) => getFieldJSX(field, index, form.controls[`${index}`]))}
      </ModalContent>
      <ModalFooter>
        {!!(file.fields || []).length && <ModalAction intent="primary" text={t`Save`} loading={submitting} autoClose={false} onClick={form.submit} />}
      </ModalFooter>
    </ModalDrawer>
  );
});

export const FormFieldsPreview = memo((props: { fields: FCFormDataType[]; values: any[] }) => {
  const { fields, values } = props;

  const FieldPreview = memo((props: { field: FCFormDataType; value: any }) => {
    const { field, value } = props;

    if (field.type === FCType.text || field.type === FCType.email || field.type === FCType.phone || field.type === FCType.paragraph) {
      const f = field as FCTextFormData;
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title}>
          <HTMLErrorBoundary>{HTMLService.toReact(value || '')} </HTMLErrorBoundary>
        </TextBlock>
      );
    } else if (field.type == FCType.date) {
      const f = field as FCDateFormData;
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title}>
          {value ? toMoment(value).format(DATE_FORMAT) : ''}
        </TextBlock>
      );
    } else if (field.type === FCType.checkbox) {
      const f = field as FCCheckboxFormData;
      const options = f.options.split('\n');
      const selectedOptions = (value || []).map((v: string) => options.find(o => o === v));
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title}>
          {selectedOptions.join(', ')}
        </TextBlock>
      );
    } else if (field.type === FCType.dropdown) {
      const f = field as FCDropdownFormData;
      const options = f.options.split('\n');
      const selectedOption = options.find(o => o === value);
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title}>
          {selectedOption}
        </TextBlock>
      );
    } else if (field.type === FCType.upload) {
      const f = field as FCUploadFormData;
      const fileName = R.get(value, 'fileName');
      const url = R.get(value, 'fileUrl');
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {fileName || ''}
          </a>
        </TextBlock>
      );
    } else if (field.type === FCType.radio) {
      const f = field as FCRadioFormData;
      const options = f.options.split('\n');
      const selectedOption = options.find(o => o === value);
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title}>
          {selectedOption}
        </TextBlock>
      );
    } else if (field.type === FCType.desciption) {
      const f = field as FCDescriptionFormData;
      return <HTMLErrorBoundary>{HTMLService.toReact(f.description || '')}</HTMLErrorBoundary>;
    } else if (field.type === FCType.title) {
      const f = field as FCTitleFormData;
      return (
        <div className="mt-4">
          <CardTitle>{f.title}</CardTitle>
          <hr />
        </div>
      );
    } else if (field.type == FCType.process) {
      const value = (props.value as ProcessFormData[]) || [];

      return (
        <div>
          <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={`Process Steps`}>
            <div></div>
          </TextBlock>
          <SRLWrapper>
            {value.map((v, index) => {
              const isTextOnly = v.type === 'text';
              const firstColumnWidth = isTextOnly ? '100%' : '50%';

              return (
                <Table key={index} className="table table-centered table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: firstColumnWidth }}>Step {index + 1}</th>
                      {!isTextOnly && <th style={{ width: '50%' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={index}>
                      {!isTextOnly && (
                        <td style={{ width: '50%' }}>
                          {v.type === 'image' && <img className="img-fluid" src={v.image} />}
                          {v.type == 'video' && (v.embedUrl || '').includes('iframe') && (
                            <div style={{ width: '100%', height: 400 }}>
                              <HTMLErrorBoundary>{HTMLService.toReact(v.embedUrl)}</HTMLErrorBoundary>
                            </div>
                          )}
                        </td>
                      )}
                      <td style={{ width: firstColumnWidth, display: 'flex', flexDirection: 'column', whiteSpace: 'initial' }}>
                        <HTMLErrorBoundary>{HTMLService.toReact(v.content || '')}</HTMLErrorBoundary>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              );
            })}
          </SRLWrapper>
        </div>
      );
      return <div>No Content</div>;
    }

    return <div></div>;
  });

  return (
    <div>
      {fields.map((field, index) => (
        <FieldPreview key={index} field={field} value={values[index]} />
      ))}
    </div>
  );
});

export const FormView = memo((props: any) => {
  const {
    fileForm,
    identity,
    handleSubmite,
    subcontractTab = null,
    isDisable = false,
    isButton = true,
    isApplicationCompleted = false,
    buttonText = 'Save',
  } = props;

  const bloc = useBloc(FormViewModalBloc, identity);
  const form = useMemo(() => defineCustomForm(fileForm.fields), [fileForm.fields]);
  const company = useCompany();

  // Local draft (localStorage): same mechanics as FormViewModal — one draft per form/tab.
  // Only enabled when a stable identity for the form exists, so unrelated forms can't collide.
  const draftKeySource = subcontractTab?.form ?? R.get(fileForm, 'id');
  const draftStore = useMemo(
    () => createLocalDraftStore(`gc-formfill-drafts-${company.id}-${Session.user?.id ?? 'anon'}`),
    [company.id],
  );
  const draftId = `formview-${draftKeySource}-${subcontractTab?.tab ?? '0'}`;
  const draftingEnabled = !!draftKeySource && !isDisable && !isApplicationCompleted;
  const [draft, setDraft] = useState<LocalDraft | null>(() => (draftingEnabled ? draftStore.load().find(d => d.id === draftId) ?? null : null));

  const saveDraftValue = useCallback((value: Record<string, any>) => draftStore.upsert(draftId, value), [draftStore, draftId]);
  const draftAutosave = useDraftAutosave(form, draftingEnabled, saveDraftValue);

  const restoreDraft = useCallback(() => {
    if (!draft) return;
    form.patchValue(draft.value as Partial<CustomFormData>);
    form.setTouched(true);
    setDraft(null);
  }, [draft, form]);

  const deleteDraft = useCallback(() => {
    draftStore.remove(draftId);
    setDraft(null);
  }, [draftStore, draftId]);

  useEffect(() => {
    if (subcontractTab.values) {
      form.patchValue(JSON.parse(subcontractTab.values));
    }
  }, [subcontractTab]);

  const onSubmit = useCallback(
    (data: CustomFormData) => {
      const clearLocalDraft = () => {
        draftAutosave.stop();
        draftStore.remove(draftId);
      };
      // Process signatures (upload base64 to get URLs)
      processSignatures(data, fileForm.fields, form).subscribe({
        next: processedData => {
          clearLocalDraft();
          handleSubmite({ value: processedData, tabId: subcontractTab?.tab, formId: subcontractTab?.form });
        },
        error: error => {
          console.error('Error processing signatures:', error);
          // If signature upload fails, still submit with base64
          clearLocalDraft();
          handleSubmite({ value: data, tabId: subcontractTab?.tab, formId: subcontractTab?.form });
        },
      });
    },
    [fileForm.fields, form, handleSubmite, subcontractTab, draftAutosave, draftStore, draftId],
  );

  useEffect(() => {
    if (fileForm.values) {
      form.patchValue(JSON.parse(fileForm.values));
    }
  }, [fileForm.values, form]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  return (
    <FormContainer onSubmit={form.submit}>
      {draftingEnabled && draft && (
        <InfoBanner intent="info" icon="fa-history">
          <span style={{ flex: 1 }}>{t`You have unsaved changes for this form from ${new Date(draft.savedAt).toLocaleString()}. Restore them?`}</span>
          <Button type="button" size="small" intent="primary" extraClassNames="ml-2" onClick={restoreDraft}>
            {t`Restore`}
          </Button>
          <Button type="button" size="small" intent="light" extraClassNames="ml-2" onClick={deleteDraft}>
            {t`Discard`}
          </Button>
        </InfoBanner>
      )}
      {fileForm.fields.map((field, index) =>
        getFieldJSX(field, index, form.controls[`${index}`], { disabled: isDisable, isApplicationCompleted }, true),
      )}
      {isButton && (
        <div className="submit mt-4" style={{ textAlign: 'right' }}>
          <Button type="submit" intent="primary" loading={submitting} disabled={isApplicationCompleted}>
            {buttonText}
          </Button>
        </div>
      )}
    </FormContainer>
  );
});
